import { useSnackbar } from "notistack";
import { useCallback, useContext, useEffect, useState } from "react";
import { NavigationContext } from "../../../Store/Navigation/context";
import { APIsContext } from "../../../Store/APIs/context";
import { AdjudicationSolved, AdjudicationSolvedOperation } from "neurotec-megamatcherid-management-client-adjudicator";
import DataTable, { HeadCell, IDataTableId } from "../../../components/Misc/DataTable/DataTable";
import { IAdjudicationSolvedFilter } from "../../../Store/Adjudication/types";
import { setLoading } from "../../../Store/Navigation/reducer";
import { Container } from "@mui/material";
import AdjudicationSolvedFilter from "../../../components/Filter/AdjudicationSolvedFilter";
import DatatableFooter from "../../../components/Misc/DataTable/DatatableFooter";
import AdjudicationOverviewDialog from "../components/AdjudicationOverviewDialog";

const headCells: HeadCell[] = [
    {
        id: 'FILTER_ADJUDICATION_SOLVED_DECISION_DATE',
        align: 'left',
        disablePadding: false,
        label: 'Date',
        width: "30%"
    },
    {
        id: 'FILTER_ADJUDICATION_SOLVED_ISSUES',
        align: 'left',
        disablePadding: false,
        label: 'Issues',
        width: "45%"
    },
    {
        id: 'FILTER_ADJUDICATION_SOLVED_FINAL_DECISION',
        align: 'left',
        disablePadding: false,
        label: 'Final decision',
        width: "25%"
    },
];

const AdjudicationSolvedTab: React.FC = () => {

    const { enqueueSnackbar } = useSnackbar();
    const navigationDispatch = useContext(NavigationContext).dispatch
    const apisState = useContext(APIsContext).state
    const [solved, setSolved] = useState<AdjudicationSolved[]>([])
    const [solvedCount, setSolvedCount] = useState<number | undefined>(undefined);
    const [selected, setSelected] = useState<IDataTableId[]>([]);
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(20)
    const [adjudicationSolvedFilter, setAdjudicationSolvedFilter] = useState<IAdjudicationSolvedFilter>({})
    const [selectedAdjudication, setSelectedAdjudication] = useState<AdjudicationSolvedOperation>();
    const [showSolvedDialog, setShowSolvedDialog] = useState(false)

    const fetchAdjudicatorSolved = useCallback(async () => {
        try {
            navigationDispatch(setLoading(true))
            apisState.AdjudicationApi.getAdjudicationsSolved({page: page, size: perPage, ...adjudicationSolvedFilter}).then(res => {
                setSolved(res.data)
                navigationDispatch(setLoading(false))
            }).catch(e => {
                console.log(e)
                enqueueSnackbar(e.message, {variant: "error"})
                navigationDispatch(setLoading(false))
            })

            apisState.AdjudicationApi.getAdjudicationsSolvedCount(adjudicationSolvedFilter).then(res => {
                setSolvedCount(res.data)
            }).catch(e => {
                setSolvedCount(0)
                console.log(e)
                enqueueSnackbar(e.message, {variant: "error"})
            })
        } catch (error: any) {
            console.log(error)
            enqueueSnackbar(error.message, {variant: "error"})
        }
    }, [page, perPage, apisState.AdjudicationApi, adjudicationSolvedFilter, enqueueSnackbar, navigationDispatch])

    useEffect(() => {
        fetchAdjudicatorSolved();
    }, [apisState.AdjudicationApi, adjudicationSolvedFilter, page, perPage, fetchAdjudicatorSolved])

    const handleSolvedClick = (solved: AdjudicationSolved) => {
        if (solved && solved.uuid) {
            apisState.AdjudicationApi.getAdjudicationSolved({uuid: solved.uuid}).then(res => {
                setSelectedAdjudication(res.data)
                setShowSolvedDialog(true)
            }).catch(e => {
                setSelectedAdjudication(undefined)
                enqueueSnackbar(e.message, {variant: "error"})
            })
        }
    }

    const handleAdjudicationSolvedDeletion = () => {
        if (selectedAdjudication?.adjudicationSolved?.uuid) {
            apisState.AdjudicationApi.deleteAdjudicationsSolved({uuids: [selectedAdjudication?.adjudicationSolved?.uuid]}).then(res => {
                if (res.data === 1) {
                    enqueueSnackbar("Solved adjudication case deleted successfully", {variant: "success"})
                    setSelectedAdjudication(undefined)
                    setShowSolvedDialog(false)
                    setAdjudicationSolvedFilter(adjudicationSolvedFilter)
                    fetchAdjudicatorSolved()
                }
            }).catch(e => {
                enqueueSnackbar(e.message, {variant: "error"})
            })
        }
    }

    const handlePageChange = (event: React.ChangeEvent<unknown> | undefined, newPage: number) => {
        setPage(newPage)
    }
    
    const handleFilterChange = (filter: IAdjudicationSolvedFilter) => {
        setPage(1)
        setSolvedCount(undefined)
        setAdjudicationSolvedFilter(filter)
    }

    return (
        <>
        <Container maxWidth="xl" sx={{ display: "flex", flexDirection: "column" }} >
            <AdjudicationSolvedFilter
                filter={adjudicationSolvedFilter}
                onFilterApply={handleFilterChange}
            />
            <DataTable
                data={solved}
                filter={adjudicationSolvedFilter}
                onFilterChange={handleFilterChange}
                headCells={headCells}
                title={"Pending adjudications"}
                dateName={"date"}
                setSelected={setSelected}
                selected={selected}
                type="adjudication-solved"
                onRowClick={handleSolvedClick}
            />
            <DatatableFooter
                page={page}
                handlePageChange={handlePageChange}
                count={solvedCount}
                perPage={perPage}
                setPerPage={setPerPage}
                label='Adjudications'
            />
        </Container>
        {selectedAdjudication &&
        (<AdjudicationOverviewDialog
            decisions
            onDelete={handleAdjudicationSolvedDeletion}
            disableAction
            open={showSolvedDialog}
            onClose={() => setShowSolvedDialog(false)}
            adjudication={selectedAdjudication}
        />)}
        </>
    )
}

export default AdjudicationSolvedTab